import * as _ from "lodash";
import * as Redux from "redux";
import * as signalR from "@microsoft/signalr";

import { ProcessCountState } from "../model/ProcessCountState";
import * as SignalRActions from "../actions/SignalRActions";
import { HashMap } from "../../shared/model/HashMap";
import { AgentProcessModel } from "../model/AgentProcessModel";
import { AgentStatusPerAgentAndProcess } from "../model/AgentStatusPerAgentAndProcess";
import * as AppPath from "../model/AppPath";
import { State } from "processList/model/State";

// tslint:disable:no-console
export class SignalrListener {

    // Too difficoult to type the store enhancers here.
    private readonly Store: Redux.Store<State, Redux.AnyAction & any>;
    private currentAgentGroup: string | null = null;
    private processNotificationHub: ISignalrListener;

    constructor(store: Redux.Store<State, Redux.AnyAction & any>, callback: () => void) {
        this.Store = store;
        this.processNotificationHub = createSignalRListener("processHub");
        this.setupProcessHub();
        this.setupStoreListener();
        callback();
    }

    private setupProcessHub() {
        this.processNotificationHub.connection.on("processUpdated", (processNotification: SignalRProcessesUpdated) => {
            console.debug("process updated", processNotification);
            const state = this.Store.getState();
            const agentGroup = _.values(state.agentGroups).find((a) => a.name === this.currentAgentGroup);
            if (!agentGroup) {
                console.error(`Could not find environment for current agent group ${this.currentAgentGroup}. Cannot update processes`);
            } else {
                this.Store.dispatch(SignalRActions.updateProcesses(
                    processNotification.type,
                    agentGroup.environment,
                    this.currentAgentGroup,
                    processNotification.processGuids));
            }
        });

        this.processNotificationHub.connection.on("processCountsUpdated", (processCountsUpdate: SignalRProcessCountsUpdated) => {
            console.debug("processCountsUpdated:", processCountsUpdate);
            if (processCountsUpdate.isFullRefresh) {
                this.Store.dispatch(SignalRActions.resetProcessCounts(processCountsUpdate.executionCounts));
            } else {
                this.Store.dispatch(SignalRActions.updateProcessCounts(processCountsUpdate.executionCounts));
            }
        });

        this.processNotificationHub.connection.on("agentStatusesUpdated", (statuses: SignalRAgentStatusesUpdated[]) => {
            const filteredStatuses = statuses.filter((s) => s.agentGroup === this.currentAgentGroup);
            const statusHashMap: AgentStatusPerAgentAndProcess = {};
            for (const status of filteredStatuses) {
                statusHashMap[status.name] = {
                    timestamp: status.timestamp,
                    triggerTypesNotInUse: status.triggerTypesNotInUse,
                    processInfos: status.processInfos
                };
            }
            console.debug("agentStatusesUpdated:", statusHashMap);
            this.Store.dispatch(SignalRActions.updateAgentStatuses(statusHashMap));
        });
    }

    private setupStoreListener() {
        this.Store.subscribe(() => {
            const state = this.Store.getState();
            const agentGroup = state.currentAgentGroupName;
            if (this.currentAgentGroup !== agentGroup) {
                this.changeAgentGroupSubscription(agentGroup);
            }
        });
        const initialAgentGroup = this.Store.getState().currentAgentGroupName;
        this.changeAgentGroupSubscription(initialAgentGroup);
    }

    private changeAgentGroupSubscription(newAgentGroup: string) {
        if (this.currentAgentGroup !== newAgentGroup) {
            console.log("Agent group changed: " + this.currentAgentGroup + " => " + newAgentGroup);
            const oldAgentGroup = this.currentAgentGroup;
            this.currentAgentGroup = newAgentGroup;
            this.processNotificationHub.startPromise.then(() => {
                this.processNotificationHub.connection.invoke("ChangeSubscribedAgentGroup", oldAgentGroup, newAgentGroup);
            });
        }
    }
}

interface SignalRProcessesUpdated {
    type: "Created" | "Updated" | "Deleted";
    processGuids: string[];
}

interface SignalRAgentStatusesUpdated {
    agentGroup: string;
    name: string;
    timestamp: string;
    triggerTypesNotInUse: string[];
    processInfos: HashMap<AgentProcessModel>;
}

interface SignalRProcessCountsUpdated {
    executionCounts: ProcessCountState;
    isFullRefresh: boolean;
}

export interface ISignalrListener {
    connection: signalR.HubConnection;
    startPromise: PromiseLike<any>;
}

const Url = !AppPath.getProcessListURL().base ? "" : `/${AppPath.getProcessListURL().base}`;
function createSignalRListener(hubName: string): ISignalrListener {
    const connection = new signalR.HubConnectionBuilder()
        .withUrl(`${Url}/${hubName}`, { skipNegotiation: true, transport: signalR.HttpTransportType.WebSockets })
        .withAutomaticReconnect()
        .configureLogging(signalR.LogLevel.Error)
        .build();

    const startPromise = connection.start().catch(err => console.error(err.toString()));

    return { connection: connection, startPromise: startPromise };
}
